import { NotMaybe } from '@/utils';
import compact from 'lodash/compact';
import { GetServerSideProps } from 'next';
import { getLookup } from 'utils/lookupHelpers';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { GetAllDataQuery } from 'queries/getAllData';
import React, { FC } from 'react';
import { SeoProps } from 'types';
import { Accordion } from '@/components/ui/Accordion';
import { Header } from '@/components/ui/Header';
import { Icon } from '@/components/ui/Icon';
import { RelatedLinks } from '@/components/ui/RelatedLinks';
import { Languages, useLanguage, withLocale } from '@/contexts/LanguageContext';
import { getRelatedLinks } from 'utils/getRelatedLinks';
import { InfoContact } from '@/components/ui/Info/Contact';
import { RichTextWithYoutubeVideos } from '@/components/ui/RichTextWithYoutubeVideos';
import { LearningModeProps } from '@/components/ui/Article';

type ForEmployersProps = SeoProps & {
  lang: Languages;
  originalLanguage?: Languages | null;
  data: NotMaybe<GetAllDataQuery['forEmployersPage']>;
  learningModeData: LearningModeProps;
};

const ForEmployersAccordion: FC<any> = ({ lang, phone, ...element }) => {
  const { title, accordion = [] } = element[lang] || {};

  return (
    <div className="flex flex-wrap mb-16 md:flex-nowrap">
      <div className="flex flex-row-reverse justify-between w-full md:block md:w-64 md:pr-5 direction-switch">
        {phone && (
          <a href={`tel:${phone}`} className="block w-16 h-16 mb-4">
            <Icon name="Phone" className="w-16 h-16" />
          </a>
        )}

        <div className="flex items-start justify-between mb-16 text-gray-700">
          <p className="text-lg">
            <span className="font-bold">{title}</span>
          </p>
        </div>
      </div>

      <Accordion items={(accordion || []).map(({ title, text }) => ({ title, content: text }))} />
    </div>
  );
};

const ForEmployersPage = (props: ForEmployersProps) => {
  const { t } = useLanguage();
  const { data, lang, originalLanguage, learningModeData } = props;

  if (!data[lang]) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <ErrorPage statusCode={404} />
      </>
    );
  }

  const relatedLinks = getRelatedLinks(data, lang);

  const downloads = compact(
    compact(data.downloads).map((download) => {
      return download[lang] || download.de;
    })
  );

  let { title, text, footer } = data[lang] ?? {};
  if (!title) title = '';
  if (!text) text = '';
  if (!footer) footer = '';

  const paragraphMatch = text.match(/(^.*?\<\/p\>)(.*?\<\/p\>)$/);

  if (paragraphMatch) {
    const beforeLinebreaks = paragraphMatch[1];
    const afterLinebreaks = paragraphMatch[2];
    text = `
    ${beforeLinebreaks}
    <img src="/static/article-illustrations/for-employers.svg" class="h-32 ml-auto mr-8 lg:hidden" alt=""/>
    ${afterLinebreaks}`;
  }

  return (
    <>
      <Header showLogoOnDesktop={true} />

      <div className="mt-16">
        <div
          className="absolute inset-0 hidden h-40 mr-8 bg-right-bottom bg-no-repeat top-32 -z-1 lg:block"
          style={{
            backgroundImage: `url("/static/article-illustrations/for-employers.svg")`,
            backgroundSize: 'contain',
          }}
        ></div>

        {originalLanguage && (
          <div className="px-4 py-3 mb-4 text-blue-700 bg-blue-100 border border-blue-500 rounded-md direction-switch" role="alert">
            <p className="font-bold">{t.article.disclaimer}</p>
          </div>
        )}

        <div className="flex flex-col items-start lg:flex-row">
          <article className="w-full mb-12 prose prose-lg lg:w-3/4 lg:pr-10 direction-switch">
            <header className="mb-10 break-words">
              <h1>{title}</h1>
            </header>
            <section dangerouslySetInnerHTML={{ __html: text }} />
          </article>

          <div className="w-full lg:w-1/4">
            <div className="hidden mb-10 prose prose-lg lg:block">
              <h1>&nbsp;</h1>
              <span></span>
            </div>

            <div className="md:w-1/2 lg:w-full">
              {compact(data.contacts_ordered).map((contact) => {
                if (!contact?.contact) return null;
                return <InfoContact className="mb-16" contact={contact.contact} key={contact.contact.id} />;
              })}
            </div>

            <div className="flex flex-wrap -mb-3 md:-mx-5 md:-mb-5 lg:m-0">
              {downloads.map((download) => (
                <div key={download.id} className="w-full mb-3 md:w-1/2 md:px-3 md:mb-5 lg:px-0 lg:mb-3 lg:w-full">
                  <a href={download.media?.url || '#'} target="_blank" download={download.title}>
                    <div className="flex items-center justify-between px-4 py-2 bg-white border-2 border-gray-900 rounded-full direction-switch">
                      <p className="ml-3 font-bold">{download.title}</p>
                      <Icon name="Download" className="w-5 h-5" />
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full mb-12 lg:w-3/4 lg:pr-10">
          {compact(data.elements).map((element) => (
            <>
              <article className="direction-switch">
                <section>
                  <RichTextWithYoutubeVideos text={element?.[lang]?.text || ''} learningModeData={learningModeData} />
                </section>
              </article>

              <ForEmployersAccordion key={element.id} {...element} lang={lang} />
            </>
          ))}
        </div>

        <div className="w-full mb-12 lg:w-3/4 lg:pr-10">
          <article className="prose prose-lg direction-switch">
            <section dangerouslySetInnerHTML={{ __html: footer }} />
          </article>
        </div>

        <RelatedLinks items={relatedLinks} className="mt-20 lg:mt-0" />
      </div>
    </>
  );
};

export default withLocale.hoc(ForEmployersPage);

export const getServerSideProps: GetServerSideProps<ForEmployersProps, { lang: Languages }> =
  withLocale.getServerSideProps<ForEmployersProps>(async (strapiLookup, lang) => {
    const forEmployers = strapiLookup.forEmployersPage;

    if (!forEmployers) {
      throw new Error('Could not create for-employers page because its data is missing from the lookup file');
    }

    const miscMeta = compact(strapiLookup.allFullMiscSites).find((miscSite) => miscSite?.slug.match(/^\/?fuer-arbeitgebende\/?$/));

    const seoProps: SeoProps = {
      title: miscMeta?.[lang]?.title,
      seoSnippet: miscMeta?.[lang]?.description,
      keywords: compact(miscMeta?.[lang]?.tags).join(', '),
    };

    const learningModeData = strapiLookup.learningMode?.Learning_mode_keyword;

    return {
      originalLanguage: forEmployers[lang] ? null : lang,
      data: forEmployers,
      ...seoProps,
      learningModeData,
    };
  }, getLookup);
